import { z } from 'zod';

const optionalString = z
  .string()
  .optional()
  .transform((value) => value || undefined);
const rawEnvSchema = z.object({
  VITE_APP_NAME: z.string().min(1).default('Emazy'),
  VITE_AUTH_MODE: z.enum(['mock', 'cognito']).optional(),
  VITE_API_BASE_URL: z.url().optional(),
  VITE_AWS_REGION: z.string().min(1).default('us-east-1'),
  VITE_COGNITO_USER_POOL_ID: optionalString,
  VITE_COGNITO_CLIENT_ID: optionalString,
  VITE_STRIPE_PUBLISHABLE_KEY: optionalString,
  VITE_CATALOG_DATA_SOURCE: z.enum(['api', 'mock']).optional(),
  VITE_CATALOG_MEDIA_BASE_URL: optionalString.pipe(z.url().optional()),
});

export type CognitoRuntimeConfig = {
  region: string;
  userPoolId: string;
  userPoolClientId: string;
};

export type AuthRuntimeConfig = {
  appName: string;
  apiBaseUrl: string;
  awsRegion: string;
  stripePublishableKey: string | undefined;
  catalogDataSource: 'api' | 'mock';
  catalogMediaBaseUrl: string | undefined;
} & (
  { authMode: 'mock'; cognito: undefined } | { authMode: 'cognito'; cognito: CognitoRuntimeConfig }
);

export function parseAuthRuntimeConfig(
  values: Record<string, unknown>,
  build: { development: boolean; production: boolean; test?: boolean },
): AuthRuntimeConfig {
  const parsed = rawEnvSchema.parse(values);
  const authMode = parsed.VITE_AUTH_MODE ?? (build.development ? 'mock' : undefined);
  if (!authMode) {
    throw new Error('VITE_AUTH_MODE must be explicitly set to cognito for production builds.');
  }
  if (build.production && authMode !== 'cognito') {
    throw new Error(
      'Production builds require VITE_AUTH_MODE=cognito; mock authentication is forbidden.',
    );
  }
  const catalogDataSource = parsed.VITE_CATALOG_DATA_SOURCE ?? (build.test ? 'mock' : 'api');
  if (build.production && catalogDataSource !== 'api') {
    throw new Error(
      'Production builds require VITE_CATALOG_DATA_SOURCE=api; mock catalog data is forbidden.',
    );
  }
  if (catalogDataSource === 'api' && !parsed.VITE_API_BASE_URL) {
    throw new Error('Catalog API mode requires VITE_API_BASE_URL.');
  }
  const base = {
    appName: parsed.VITE_APP_NAME,
    apiBaseUrl: parsed.VITE_API_BASE_URL ?? 'http://localhost:3000',
    awsRegion: parsed.VITE_AWS_REGION,
    stripePublishableKey: parsed.VITE_STRIPE_PUBLISHABLE_KEY,
    catalogDataSource,
    catalogMediaBaseUrl: parsed.VITE_CATALOG_MEDIA_BASE_URL,
  };
  if (authMode === 'mock') return { ...base, authMode, cognito: undefined };
  if (!parsed.VITE_COGNITO_USER_POOL_ID || !parsed.VITE_COGNITO_CLIENT_ID) {
    throw new Error('Cognito mode requires VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID.');
  }
  return {
    ...base,
    authMode,
    cognito: {
      region: parsed.VITE_AWS_REGION,
      userPoolId: parsed.VITE_COGNITO_USER_POOL_ID,
      userPoolClientId: parsed.VITE_COGNITO_CLIENT_ID,
    },
  };
}

export const env = parseAuthRuntimeConfig(import.meta.env, {
  development: import.meta.env.DEV,
  production: import.meta.env.PROD,
  test: import.meta.env.MODE === 'test',
});
